/*----------------------------------
- DOMAINS LANDING CTA SECTION
----------------------------------*/

/**
 * The footer conversion block, built to the home hero's own shape: centered headline with the
 * marker highlight, one paragraph, the persona pair, the screener film full width, then the
 * producer-backed statistics as a row. No card around it, so it reads as the same section the
 * home page opens with.
 *
 * Most visitors arrive cold from ChatGPT, GitHub or Google looking for a "one word domains
 * database", so the copy's one job is to say that the database is the entry point and not the
 * product: every name is vetted (price, demand, history, trademark risk, estimated value) and a
 * saved search keeps working after they leave. It claims only what the product and Full Access
 * really do, so the facts stay in step with the home hero and the pricing catalog (ADR-0083: no
 * free plan, so the paragraph ends on the price and the refund, read from their constants).
 *
 * The page H1 lives in the title block above the table, so this heading is an h2.
 */

import PersonaAuthCtaPair from '@/client/pages/landing/components/conversion/PersonaAuthCtaPair';
import HomeScreenerBlueprintMotion from '@/client/pages/landing/components/sections/product/HomeScreenerBlueprintMotion';
import { formatCompactStat } from '@/client/pages/landing/lib/formatCompactStat';
import {
    BROWSE_ESTIMATE_SPOKEN_LABEL,
    BROWSE_ESTIMATE_SYMBOL,
} from '@uniquedomains/components/radar/catalogs/browseResultCountCopy';
import type { DatabaseBrowseCountPrecision } from '@uniquedomains/components/contracts/domains/browseFacets';
import { PUBLIC_PLAN_FACTS } from '@uniquedomains/components/pricing/catalogs/pricingPlans';
import { REFUND_GUARANTEE_DAYS } from '@uniquedomains/components/pricing/catalogs/refundPromise';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsLandingCtaSectionProps = {
    medianAsk: number | null;
    totalCountLabel: string | null;
    /** Adds the spoken "(estimated)" to the tracked-names tile when the total is sampled. */
    countPrecision: DatabaseBrowseCountPrecision;
    totalTlds: number | null;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const DomainsLandingCtaSection = ({ medianAsk, totalCountLabel, countPrecision, totalTlds }: DomainsLandingCtaSectionProps) => {
    const stats = [
        totalCountLabel
            ? { label: 'One-word domains tracked', value: totalCountLabel, isEstimate: countPrecision === 'sampled' }
            : null,
        // Drawn from the same page sample as the count on a catalog-wide search, so it carries the
        // same mark (Gaetan, D1 of 2026-10-12): it moves between nights.
        medianAsk
            ? {
                  label: 'Median ask',
                  value: `${countPrecision === 'sampled' ? BROWSE_ESTIMATE_SYMBOL : ''}$${formatCompactStat(medianAsk)}`,
                  isEstimate: countPrecision === 'sampled',
              }
            : null,
        totalTlds ? { label: 'TLDs tracked', value: formatCompactStat(totalTlds), isEstimate: false } : null,
    ].filter((stat): stat is { label: string; value: string; isEstimate: boolean } => Boolean(stat));

    return (
        <>
            <section
                data-testid="domains-footer-product-entry-card"
                class="mx-auto w-full max-w-[1240px] px-4 pb-6 pt-16 sm:px-6 sm:pb-8 lg:px-8 lg:pt-20"
            >
                <div class="mx-auto max-w-[960px] text-center">
                    {/* The home hero's two-line shape: the question at full size with the marker on
                        the phrase the visitor searched for, the answer one step smaller and muted, so
                        the highlight stays a highlight rather than a bar across a whole line. */}
                    <h2 class="text-[clamp(2.25rem,5.4vw,3.4rem)] font-medium leading-[1.05] tracking-[-0.035em] text-slate-900">
                        <span class="block">
                            You wanted a list of{' '}
                            <span class="ud-landing-headline-highlight">one-word domains</span>?
                        </span>
                        <span class="mt-3 block text-[0.6215em] text-[#666]">
                            This one <span class="text-[#333]">vets and tracks them</span> for you.
                        </span>
                    </h2>

                    {/* "asking prices", not "live asks": the scan samples one compatible registrar
                        per name, so no figure on this page is live (ADR-0032). "Estimated values",
                        not "resale ranges": the home-grown estimators were retired on 2026-07-25. */}
                    <p class="mx-auto mt-6 max-w-[620px] text-base leading-[1.65] text-slate-600 sm:text-lg">
                        Search {totalCountLabel ? `${totalCountLabel} names` : 'every name'} by prompt or filters.
                        Price, demand, history, trademark risk and estimated value on every one.
                        Full Access is ${PUBLIC_PLAN_FACTS.member.price} a month, money back within {REFUND_GUARANTEE_DAYS} days.
                    </p>

                    <div class="mt-8 flex flex-wrap items-center justify-center gap-3">
                        <PersonaAuthCtaPair
                            attribution="website_domains_footer"
                            investorTriggerId="website_domains_footer_investor"
                            founderTriggerId="website_domains_footer_founder"
                            page="domains_public"
                            section="domains_footer"
                            size="lg"
                            surface="footer"
                            tone="light"
                        />
                    </div>
                </div>
            </section>

            {/* The film follows the centered block directly, exactly as it does on the home page. */}
            <section class="mx-auto max-w-[1240px] px-4 pb-2 pt-2 sm:px-6 sm:pb-3 sm:pt-3 lg:px-8 lg:pt-4">
                <HomeScreenerBlueprintMotion />
            </section>

            {stats.length ? (
                <section class="mx-auto flex max-w-[1240px] flex-col items-center gap-[18px] px-4 pb-6 pt-4 sm:px-6 sm:pb-8 sm:pt-5 lg:px-8 lg:pb-10 lg:pt-6">
                    <dl
                        class="flex flex-wrap items-start justify-center gap-x-12 gap-y-6 sm:gap-x-24"
                        aria-label="One-word domain market statistics"
                    >
                        {stats.map((stat) => (
                            <div key={stat.label} class="ud-landing-hero-stat min-w-[140px] text-center">
                                <dd class="ud-landing-hero-stat-value">
                                    {stat.value}
                                    {stat.isEstimate ? <span class="sr-only"> {BROWSE_ESTIMATE_SPOKEN_LABEL}</span> : null}
                                </dd>
                                <dt class="ud-landing-hero-stat-label">{stat.label}</dt>
                            </div>
                        ))}
                    </dl>
                </section>
            ) : null}
        </>
    );
};

export default DomainsLandingCtaSection;
